import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { SafeId } from './safe-id'
import type { Statement } from './statement'

/**
 * A `${scope.<name>}` placeholder a role's statements may contain. Only these
 * names are substituted.
 */
export type Variable = (typeof Variable.ALL)[number]
export namespace Variable {
  export const ALL = ['tenant', 'zone', 'app', 'scenario', 'agent'] as const
  export const isVariable = TypeChecker.Factory.oneOf(ALL)
  export const assertIsVariable: TypeAsserter<Variable> =
    TypeAsserter.Factory.from(isVariable)

  /** The placeholder as it appears inside a role's resource */
  export function placeholder<TVariable extends Variable>(
    variable: TVariable,
  ): `\${scope.${TVariable}}` {
    return `\${scope.${variable}}`
  }
}

/**
 * The variable names a role's statements require. Derived from the statements
 * on write: every variable used must be declared, and every one declared must
 * be used. A role whose contract is empty is a concrete role.
 */
export type Contract = readonly Variable[]

/** The variables the statements' resources use, in {@link Variable.ALL} order */
export function contractOf(
  statements: readonly Statement.Templated[],
): Contract {
  const resources = statements.flatMap(({ resources: r }) => r)
  return Variable.ALL.filter(variable =>
    resources.some(resource =>
      resource.includes(Variable.placeholder(variable)),
    ),
  )
}

/**
 * The values a role assignment supplies for its role's {@link Contract}:
 * every declared variable, nothing extra.
 *
 * With a specific contract every variable is required
 * (`Binding<'tenant' | 'zone'>`). Without one — a stored assignment whose role
 * is not yet loaded — each is optional, and completeness is checked against
 * the role.
 */
export type Binding<TVariable extends Variable = Variable> =
  Variable extends TVariable
    ? Readonly<Partial<Record<Variable, SafeId>>>
    : Readonly<Record<TVariable, SafeId>>
